import { useState } from "react";
import { Asset } from "@/schemas";
import { ChevronDown, X, BarChart3 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { fetchWithAuth } from "@/lib/api";
import { cn } from "@/lib/utils";
import { API_BASE } from "@/config/api";
import { AssetLogo } from "@/components/asset/asset-logo";

// Common benchmark suggestions
const POPULAR_BENCHMARKS = [
  { symbol: "SPY", name: "S&P 500" },
  { symbol: "QQQ", name: "NASDAQ 100" },
  { symbol: "IWM", name: "Russell 2000" },
  { symbol: "VTI", name: "Total US Market" },
  { symbol: "EFA", name: "EAFE" },
];

interface BenchmarkSelectorProps {
  enabled: boolean;
  onEnabledChange: (enabled: boolean) => void;
  selectedAsset: Asset | null;
  onAssetSelect: (asset: Asset | null) => void;
  loading?: boolean;
  className?: string;
}

export function BenchmarkSelector({
  enabled,
  onEnabledChange,
  selectedAsset,
  onAssetSelect,
  loading = false,
  className,
}: BenchmarkSelectorProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [searchResults, setSearchResults] = useState<Asset[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);

  const handleSearch = async (searchQuery: string) => {
    setQuery(searchQuery);
    
    if (searchQuery.length < 2) {
      setSearchResults([]);
      return;
    }

    setSearchLoading(true);
    try {
      const url = new URL(`${API_BASE}/assets/search`);
      url.searchParams.append("asset_types", "stock,etf,index");
      url.searchParams.append("q", searchQuery);

      const response = await fetchWithAuth(url.toString(), {
        method: "GET",
      });
      const data = await response.json();
      setSearchResults(data.results || []);
    } catch (error) {
      console.error("Error searching assets:", error);
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleAssetSelect = (asset: Asset) => {
    onAssetSelect(asset);
    setOpen(false);
    setQuery("");
    setSearchResults([]);
    // Auto-enable when selecting an asset
    if (!enabled) {
      onEnabledChange(true);
    }
  };

  const handlePopularSelect = async (symbol: string) => {
    setSearchLoading(true);
    try {
      const url = new URL(`${API_BASE}/assets/search`);
      url.searchParams.append("asset_types", "stock,etf");
      url.searchParams.append("q", symbol);

      const response = await fetchWithAuth(url.toString(), {
        method: "GET",
      });
      const data = await response.json();
      
      if (data.results && data.results.length > 0) {
        const exactMatch = data.results.find(
          (a: Asset) => a.symbol.toUpperCase() === symbol.toUpperCase()
        );
        handleAssetSelect(exactMatch || data.results[0]);
      }
    } catch (error) {
      console.error("Error fetching popular benchmark:", error);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleClear = () => {
    onAssetSelect(null);
    onEnabledChange(false);
  };

  return (
    <div className={cn("flex items-center gap-1", className)}>
      {/* Benchmark selector pill */}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant={enabled && selectedAsset ? "secondary" : "ghost"}
            size="sm"
            className={cn(
              "h-7 px-2.5 text-xs font-medium rounded-full transition-all",
              enabled && selectedAsset 
                ? "bg-orange-500/10 text-orange-600 hover:bg-orange-500/20 border border-orange-500/20" 
                : "text-muted-foreground hover:text-foreground"
            )}
          >
            <BarChart3 className="h-3 w-3 mr-1.5" />
            {selectedAsset ? selectedAsset.symbol : "Compare"}
            {!selectedAsset && <ChevronDown className="h-3 w-3 ml-1 opacity-50" />}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[260px] p-0" align="end">
          <Command>
            <CommandInput
              placeholder="Search benchmark..."
              value={query}
              onValueChange={handleSearch}
              className="text-sm"
            />
            <CommandList className="max-h-[240px]">
              {query.length >= 2 ? (
                <>
                  {searchLoading ? (
                    <div className="py-6 text-center text-sm text-muted-foreground">
                      Searching...
                    </div>
                  ) : (
                    <>
                      <CommandEmpty>No results</CommandEmpty>
                      <CommandGroup>
                        {searchResults.slice(0, 8).map((asset) => (
                          <CommandItem
                            key={asset.asset_id}
                            value={`${asset.symbol}-${asset.name}`}
                            onSelect={() => handleAssetSelect(asset)}
                            className="cursor-pointer"
                          >
                            <div className="flex items-center gap-2">
                              <AssetLogo asset={asset} size="sm" />
                              <div className="flex flex-col">
                                <span className="font-medium text-sm">{asset.symbol}</span>
                                <span className="text-xs text-muted-foreground truncate max-w-[160px]">
                                  {asset.name}
                                </span>
                              </div>
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </>
                  )}
                </>
              ) : (
                <CommandGroup heading="Popular">
                  {POPULAR_BENCHMARKS.map((bench) => (
                    <CommandItem
                      key={bench.symbol}
                      value={`${bench.symbol}-${bench.name}`}
                      onSelect={() => handlePopularSelect(bench.symbol)}
                      className="cursor-pointer"
                    >
                      <div className="flex items-center justify-between w-full">
                        <span className="font-medium text-sm">{bench.symbol}</span>
                        <span className="text-xs text-muted-foreground">
                          {bench.name}
                        </span>
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {/* Separate clear button (outside popover trigger) */}
      {selectedAsset && enabled && (
        <Button
          variant="ghost"
          size="sm"
          onClick={handleClear}
          className="h-6 w-6 p-0 rounded-full text-orange-600 hover:bg-orange-500/20 hover:text-orange-600"
        >
          <X className="h-3 w-3" />
        </Button>
      )}

      {loading && (
        <div className="h-3 w-3 border-2 border-muted-foreground/30 border-t-muted-foreground rounded-full animate-spin" />
      )}
    </div>
  );
}
